'use client'

import '../../sass/menu/LanguageFlag.scss'
import { Language } from '@/lib/interfaces/interfaces'
import { LANGUAGE_LABELS } from '@/lib/utils/languages'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'

interface Props {
  slug: string
  language: Language
  defaultLanguage: Language
  activeLanguages: Language[]
}

const LanguageFlag = ({ slug, language, defaultLanguage, activeLanguages }: Props) => {
  const searchParams = useSearchParams()
  const isDelivery = searchParams.get('order') === 'delivery'
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  let defaultEnglishFlag = '/img/flags/us-flag.svg'

  if (typeof window !== 'undefined') {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timeZone.includes('Europe')) {
      defaultEnglishFlag = '/img/flags/en-flag.svg' // Bandera de Reino Unido
    }
  }

  const [englishFlag] = useState(defaultEnglishFlag)

  // defaultLanguage + activos, sin duplicados
  const availableLanguages = [defaultLanguage, ...activeLanguages].filter(
    (lang, index, self) => self.indexOf(lang) === index
  )
  const otherLanguages = availableLanguages.filter(lang => lang !== language)

  // Cerrar el dropdown al hacer click fuera
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const getFlagSrc = (lang: Language) => {
    if (lang === 'en') return englishFlag
    return `/img/flags/${lang}-flag.svg`
  }

  // Si solo hay un idioma disponible, no mostrar nada
  if (otherLanguages.length === 0) return null

  return (
    <div className='language-selector' ref={containerRef}>
      <button
        type='button'
        className='language-selector__trigger'
        onClick={() => setOpen(o => !o)}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={`Idioma actual: ${LANGUAGE_LABELS[language]}`}
      >
        <Image
          className='language-selector__flag'
          src={getFlagSrc(language)}
          width={180}
          height={60}
          alt={LANGUAGE_LABELS[language]}
        />
        <span className={`language-selector__chevron ${open ? 'language-selector__chevron--open' : ''}`} aria-hidden='true'>▾</span>
      </button>

      {open && (
        <ul className='language-selector__list' role='listbox'>
          {otherLanguages.map(lang => (
            <li key={lang} role='option' aria-selected='false'>
              <Link
                prefetch={false}
                href={`/menu/${slug}/${lang}${isDelivery ? '?order=delivery' : ''}`}
                className='language-selector__option'
                onClick={() => setOpen(false)}
              >
                <Image
                  className='language-selector__flag'
                  src={getFlagSrc(lang)}
                  width={180}
                  height={60}
                  alt={LANGUAGE_LABELS[lang]}
                />
                <span className='language-selector__name'>{LANGUAGE_LABELS[lang]}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LanguageFlag
